'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Chip } from '@/components/ui/chip';
import { CircularProgress } from '@/components/ui/circularProgress';
import { IconButton } from '@/components/ui/iconButton';
import { CloseIcon } from '@/components/ui/icons';
import { TextField } from '@/components/ui/textField';
import { translate } from '@/i18n';
import { listingTranslate } from '@/i18n/translations/listingTranslations';
import { GeolocationCallbacks, getDevicePosition } from '@/lib/getDevicePosition';

interface LocationSearchInputProps extends GeolocationCallbacks {
  userCoordinates?: GeolocationCoordinates;
  /** called when value changes (debounced) */
  onChangeCallback: (value: string) => void;
  onClearUserLocation?: () => void;
}

const CustomTargetIcon = dynamic(() => import('@/components/icons/customTargetIcon'));

export const LocationSearchInput = ({
  onChangeCallback,
  onGeoLocationUnavailable,
  onGetGeoCoordinatesError,
  onGetGeoCoordinates: onGetGeoCoordinatesInput,
  onGetGeoCoordinatesLoadingStateChange: onGetGeoCoordinatesLoadingStateChangeInput,
  onClearUserLocation: onClearUserLocationInput,
  userCoordinates: userCoordinatesInput
}: LocationSearchInputProps) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useDebounce('', 400);
  const [userCoordinates, setUserCoordinates] = useState<GeolocationCoordinates | undefined>(
    undefined
  );

  const onGetGeoCoordinatesLoadingStateChange = (state: boolean) => {
    setLoading(state);
    onGetGeoCoordinatesLoadingStateChangeInput && onGetGeoCoordinatesLoadingStateChangeInput(state);
  };

  const onGetGeoCoordinates = (cords: GeolocationCoordinates) => {
    onGetGeoCoordinatesInput && onGetGeoCoordinatesInput(cords);
    setUserCoordinates(cords);
  };

  const clearUserLocation = () => {
    setUserCoordinates(undefined);
    setValue('');
    onClearUserLocationInput && onClearUserLocationInput();
  };

  const handleGetLocation = () => {
    getDevicePosition({
      onGeoLocationUnavailable,
      onGetGeoCoordinates,
      onGetGeoCoordinatesError,
      onGetGeoCoordinatesLoadingStateChange
    });
  };

  useEffect(() => {
    setDebouncedValue(value);
  }, [value]);

  useEffect(() => {
    onChangeCallback(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    onGetGeoCoordinatesLoadingStateChange && onGetGeoCoordinatesLoadingStateChange(loading);
  }, [loading]);

  useEffect(() => {
    setUserCoordinates(userCoordinatesInput);
  }, [userCoordinatesInput]);

  return !userCoordinates ? (
    <TextField
      suppressHydrationWarning
      id="outlined-basic"
      label={translate('shared.locations.title')}
      placeholder={listingTranslate('locations.search.placeholder')}
      focused
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      endAdornment={
        <IconButton
          edge="end"
          size="small"
          className="hover:text-(--mui-palette-primary-main)"
          onClick={!value.length ? handleGetLocation : () => setValue('')}
          disabled={!navigator.geolocation || !!userCoordinates || loading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : !value.length ? (
            <CustomTargetIcon className="size-6" />
          ) : (
            <CloseIcon />
          )}
        </IconButton>
      }
    />
  ) : (
    <div className="flex w-full items-center">
      <Chip
        label={listingTranslate('locations.search.my.location')}
        variant="outlined"
        onDelete={clearUserLocation}
        deleteAriaLabel={listingTranslate('locations.search.my.location.remove.aria-label')}
      />
    </div>
  );
};
